import React, { useState, useEffect } from "react";
import { FiCheckCircle, FiShield, FiLock, FiTag, FiX, FiChevronDown, FiChevronUp } from "react-icons/fi";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import axiosInstance from "../../utils/axiosInstance";
import "../Cart/cartpage.css";
import "./checkout-coupon.css";
import Breadcrumbs from "../Breadcrumbs";

const formatMoney = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "Quote on request";
  return `$${number.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const getLinePrice = (item) => {
  // Prefer API-calculated offerPrice (from rental_charges final_cost) over raw package price
  if (item.offerPrice != null) {
    return Number(item.offerPrice);
  }
  if (item.selectedPackage && item.selectedPackage.offer_price) {
    return Number(item.selectedPackage.offer_price);
  }
  return Number(item.sale_offer_price ?? item.sale_price ?? item.oldPrice ?? item.rental_price ?? 0);
};

const getEffectiveApplied = (type, currentApplied, orderObj) => {
  if (currentApplied && currentApplied.code) return currentApplied;
  if (!orderObj || !orderObj.coupon_code) return null;
  const list = Array.isArray(orderObj.coupon_code) ? orderObj.coupon_code : [];
  const found = list.find(c => (type === "sale" ? c.coupon_type === "sale" : (c.coupon_type === "rental" || c.coupon_type === "rent")) && !c.is_deleted && c.status_flag !== false);
  if (found && (found.coupon_code || found.code)) {
    return { code: found.coupon_code || found.code, id: found.coupon_applied ?? found.id, discount: found.coupon_discount };
  }
  return null;
};

const CouponSection = ({
  type,
  coupons,
  applied,
  onApply,
  onRemove,
  expanded,
  setExpanded,
  inputVal,
  setInputVal,
  applyingCouponId,
  orderInfo,
  couponError,
}) => {
  const manualSentinel = `manual-${type}`;
  const isManualApplying = applyingCouponId === manualSentinel;
  const effectiveApplied = getEffectiveApplied(type, applied, orderInfo);
  const isInputApplied = effectiveApplied && inputVal && effectiveApplied.code?.trim().toLowerCase() === inputVal.trim().toLowerCase();

  return (
    <div className="co-coupon-section">
      <button
        type="button"
        className="co-coupon-toggle"
        onClick={() => setExpanded(v => !v)}
      >
        <span className="co-coupon-toggle-left">
          <FiTag className="co-coupon-icon" />
          <span>{type === "sale" ? "Sale" : "Rental"} Coupons</span>
          {effectiveApplied && <span className="co-coupon-applied-badge">Applied</span>}
        </span>
        {expanded ? <FiChevronUp /> : <FiChevronDown />}
      </button>

      {expanded && (
        <div className="co-coupon-body">
          {effectiveApplied && (
            <div className="co-coupon-card is-applied mb-3" style={{ border: "2px solid #16a34a", backgroundColor: "#f0fdf4" }}>
              <div className="co-coupon-card-body">
                <p className="co-coupon-desc" style={{ color: "#15803d", fontWeight: "600" }}>Currently Applied Coupon</p>
                <div className="co-coupon-ticket">
                  <span className="co-coupon-code" style={{ backgroundColor: "#16a34a", color: "#fff" }}>{effectiveApplied.code}</span>
                  <button
                    type="button"
                    className="co-coupon-btn remove"
                    onClick={() => onRemove(type)}
                    disabled={isManualApplying}
                  >
                    <FiX size={12} /> Remove
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="co-coupon-input-row">
            <input
              type="text"
              placeholder={`Enter ${type} coupon code`}
              value={inputVal}
              onChange={e => setInputVal(e.target.value)}
              className="co-coupon-input"
            />
            <button
              type="button"
              className={`co-coupon-apply-btn ${isInputApplied ? "remove-mode" : ""}`}
              style={isInputApplied ? { backgroundColor: "#ef4444", borderColor: "#ef4444" } : {}}
              onClick={() => isInputApplied ? onRemove(type) : onApply(inputVal, null, type)}
              disabled={!inputVal || isManualApplying}
            >
              {isManualApplying ? "Applying..." : isInputApplied ? "Remove" : "Apply"}
            </button>
          </div>

          {couponError && <p className="co-coupon-error">{couponError}</p>}

          <div className="co-coupon-list">
            {coupons.length === 0 ? (
              <p style={{ fontSize: "12px", color: "#9ca3af", fontStyle: "italic", margin: "8px 0 0" }}>
                No featured coupon cards currently listed. You can still enter your coupon code above.
              </p>
            ) : (
              coupons.map(coupon => {
                const isApplied = effectiveApplied && (
                  (effectiveApplied.id != null && effectiveApplied.id === coupon.id) ||
                  (effectiveApplied.code && coupon.code && effectiveApplied.code.trim().toLowerCase() === coupon.code.trim().toLowerCase())
                );

                if (isApplied) return null;

                const isThisApplying = applyingCouponId === coupon.id;
                return (
                  <div className={`co-coupon-card ${isApplied ? "is-applied" : ""}`} key={coupon.id}>
                    <div className="co-coupon-card-body">
                      <p className="co-coupon-desc">{coupon.description || "Special Offer"}</p>
                      <div className="co-coupon-ticket">
                        <span className="co-coupon-code">{coupon.code}</span>
                        <button
                          type="button"
                          className={`co-coupon-btn ${isApplied ? "remove" : "apply"}`}
                          onClick={() => isApplied ? onRemove(type) : onApply(coupon.code, coupon.id, type)}
                          disabled={isThisApplying}
                        >
                          {isThisApplying ? "Applying..." : isApplied ? <><FiX size={12} /> Remove</> : "Apply"}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

function Checkout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { buyCart = [], rentCart = [] } = useSelector((store) => store.cart || {});

  // Coupons received from CartPage navigation state
  const [saleCoupons, setSaleCoupons] = useState(location.state?.saleCoupons || []);
  const [rentalCoupons, setRentalCoupons] = useState(location.state?.rentalCoupons || []);
  const [appliedSaleCoupon, setAppliedSaleCoupon] = useState(null); // { code, id }
  const [appliedRentalCoupon, setAppliedRentalCoupon] = useState(null);
  const [couponExpandSale, setCouponExpandSale] = useState(false);
  const [couponExpandRental, setCouponExpandRental] = useState(false);
  const [saleDiscount, setSaleDiscount] = useState(0);
  const [rentalDiscount, setRentalDiscount] = useState(0);
  const [couponInputSale, setCouponInputSale] = useState("");
  const [couponInputRental, setCouponInputRental] = useState("");
  const [couponError, setCouponError] = useState("");
  const [applyingCouponId, setApplyingCouponId] = useState(null); // id of coupon currently being applied

  const saleAddressRaw = localStorage.getItem("saleAddress");
  const rentalAddressRaw = localStorage.getItem("rentalAddress");
  const defaultAddressId = localStorage.getItem("defaultAddressId");

  const saleAddressId = saleAddressRaw ? JSON.parse(saleAddressRaw)?.id : (defaultAddressId ? Number(defaultAddressId) : null);
  const rentalAddressId = rentalAddressRaw ? JSON.parse(rentalAddressRaw)?.id : "";
  const allItems = [
    ...buyCart.map((item) => ({ ...item, cartType: "Sale" })),
    ...rentCart.map((item) => ({ ...item, cartType: "Rental" })),
  ];
  const subtotal = allItems.reduce((sum, item) => sum + getLinePrice(item) * (item.qty || 1), 0);
  const totalQuantity = allItems.reduce((sum, item) => sum + (item.qty || 1), 0);

  const deliveryItemsPayload = allItems.map((item) => {
    const isSale = item.cartType?.toLowerCase() === "sale";
    const itemObj = {
      cart_type: isSale ? "buy" : "rental",
      quantity: item.qty || 1,
    };
    if (isSale) {
      itemObj.sale_address_id = saleAddressId;
    } else {
      itemObj.rental_address_id = rentalAddressId;
    }
    return itemObj;
  });

  const [deliveryMode, setDeliveryMode] = useState(() => localStorage.getItem("deliveryMode") || "company-transport");
  const [deliveryCharge, setDeliveryCharge] = useState(null);
  const [displayedDeliveryCharge, setDisplayedDeliveryCharge] = useState(null);
  const [updatingDeliveryMode, setUpdatingDeliveryMode] = useState(false);
  const [orderInfo, setOrderInfo] = useState(null);
  const [orderDetails, setOrderDetails] = useState([]);
  const [loadingOrder, setLoadingOrder] = useState(false);

  // Fetch coupons if not passed via navigation state
  useEffect(() => {
    if (saleCoupons.length === 0 && rentalCoupons.length === 0) {
      Promise.allSettled([
        axiosInstance.get("catlog/coupons/?coupon_type=sale&is_suspended=false"),
        axiosInstance.get("catlog/coupons/?coupon_type=rental&is_suspended=false"),
      ]).then(([saleRes, rentalRes]) => {
        if (saleRes.status === "fulfilled") setSaleCoupons(saleRes.value?.data?.results || []);
        if (rentalRes.status === "fulfilled") setRentalCoupons(rentalRes.value?.data?.results || []);
      }).catch(() => { });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyCoupon = async (code, couponId, type) => {
    if (!code) return;
    const orderIdRaw = localStorage.getItem("orderId");
    const orderId = (!orderIdRaw || orderIdRaw === "null" || orderIdRaw === "undefined") ? null : orderIdRaw;

    // Look up couponId if not provided (when entered via manual input box)
    let matchedId = couponId;
    if (matchedId == null) {
      const list = type === "sale" ? saleCoupons : rentalCoupons;
      const found = list.find(c => c.code?.trim().toLowerCase() === code.trim().toLowerCase());
      if (found) matchedId = found.id;
    }

    const trackingId = matchedId ?? `manual-${type}`;
    setCouponError("");
    setApplyingCouponId(trackingId);
    try {
      const res = await axiosInstance.post("/accounts/orders/apply-coupon/", {
        order_id: orderId,
        coupon_code: code,
      });
      const order = Array.isArray(res.data) ? res.data[0] : (res.data?.order || res.data || {});
      // Update orderInfo so grand total recalculates from API
      if (order && order.id) setOrderInfo(order);
      const sDiscount = Number(order?.sale_discount_amount || 0);
      const rDiscount = Number(order?.rental_discount_amount || 0);
      setSaleDiscount(sDiscount);
      setRentalDiscount(rDiscount);

      if (type === "sale") {
        setAppliedSaleCoupon({ code, id: matchedId });
        setCouponInputSale(code);
      } else {
        setAppliedRentalCoupon({ code, id: matchedId });
        setCouponInputRental(code);
      }

      toast.success(res.data?.message || "Coupon applied successfully");

    } catch (err) {
      const data = err.response?.data || {};
      // API returns errors as arrays: { message: ["..."], code: [...] }
      const msg =
        (Array.isArray(data.message) ? data.message[0] : data.message) ||
        (Array.isArray(data.non_field_errors) ? data.non_field_errors[0] : data.non_field_errors) ||
        (Array.isArray(data.error) ? data.error[0] : data.error) ||
        "Failed to apply coupon";
      toast.error(msg);
    } finally {
      setApplyingCouponId(null);
    }
  };

  const removeCoupon = async (type) => {
    const orderIdRaw = localStorage.getItem("orderId");
    const orderId = (!orderIdRaw || orderIdRaw === "null" || orderIdRaw === "undefined") ? null : orderIdRaw;

    const couponCode = type === "sale" ? appliedSaleCoupon?.code : appliedRentalCoupon?.code;

    // Optimistically clear UI state
    if (type === "sale") {
      setAppliedSaleCoupon(null);
      setSaleDiscount(0);
      setCouponInputSale("");
    } else {
      setAppliedRentalCoupon(null);
      setRentalDiscount(0);
      setCouponInputRental("");
    }

    try {
      const res = await axiosInstance.post("/accounts/orders/remove-coupon/", {
        order_id: orderId,
        coupon_code: couponCode || "",
        coupon_type: type,
      });

      const order = Array.isArray(res.data) ? res.data[0] : (res.data?.order || res.data || {});
      // Update orderInfo so grand total recalculates from API
      if (order && order.id) setOrderInfo(order);
      // Sync discounts from API response
      setSaleDiscount(Number(order?.sale_discount_amount || 0));
      setRentalDiscount(Number(order?.rental_discount_amount || 0));

      toast.success(res.data?.message || "Coupon removed successfully");
    } catch (err) {
      const data = err.response?.data || {};
      const msg =
        (Array.isArray(data.message) ? data.message[0] : data.message) ||
        (Array.isArray(data.error) ? data.error[0] : data.error) ||
        "Failed to remove coupon";
      toast.error(msg);
    }
  };

  const syncOrderAndCharges = async (mode) => {
    if (allItems.length === 0) return;
    setLoadingOrder(true);
    try {
      // 1. Call Order API (PATCH or POST)
      const existingOrderId = localStorage.getItem("orderId");
      const orderPayload = {
        sale_addresses: buyCart.length > 0 ? (saleAddressId || "") : "",
        rental_addresses: rentCart.length > 0 ? (rentalAddressId || "") : "",
        delivery_mode: mode,
        order_details: allItems.map((item) => {
          const isSale = item.cartType?.toLowerCase() === "sale";
          const variantId = item.rentalCharges?.variant || item.variantId || item.id;
          const obj = {
            variant: Number(variantId),
            quantity: item.qty || 1,
            item_type: isSale ? "sale" : "rental",
          };
          if (!isSale) {
            obj.rental_start_date = item.fromDate || item.startDate || item.rental_start_date || new Date().toISOString().split("T")[0];
            if (item.toDate || item.rentalCharges?.rental_end_date) {
              obj.rental_end_date = item.toDate || item.rentalCharges?.rental_end_date;
            }
            if (item.selectedPackage?.package_name || item.rentalPlan || item.package_name) {
              obj.package_name = item.selectedPackage?.package_name || item.rentalPlan || item.package_name;
            }
            if (item.selectedPackage?.duration_value || item.duration || item.duration_value) {
              obj.duration_value = item.selectedPackage?.duration_value || item.duration || item.duration_value;
            }
            if (item.cartItemId || item.unique_id) {
              obj.unique_id = item.cartItemId || item.unique_id;
            }
          }
          return obj;
        }),
      };

      let currentOrderId = existingOrderId;
      let orderRes;
      if (existingOrderId && existingOrderId !== "null" && existingOrderId !== "undefined") {
        orderRes = await axiosInstance.patch(`/accounts/orders/${existingOrderId}/`, orderPayload);
      } else {
        orderRes = await axiosInstance.post(`/accounts/orders/`, orderPayload);
        currentOrderId = Array.isArray(orderRes?.data) ? orderRes.data[0]?.id : (orderRes?.data?.id || orderRes?.data?.order?.id);
        if (currentOrderId) {
          localStorage.setItem("orderId", currentOrderId);
        }
      }
      if (orderRes?.data) {
        const syncedOrder = Array.isArray(orderRes.data) ? orderRes.data[0] : (orderRes.data?.order || orderRes.data);
        setOrderInfo(syncedOrder);
        if (syncedOrder) {
          if (syncedOrder.sale_discount_amount != null) setSaleDiscount(Number(syncedOrder.sale_discount_amount || 0));
          if (syncedOrder.rental_discount_amount != null) setRentalDiscount(Number(syncedOrder.rental_discount_amount || 0));
        }
      }

      // 2. Call Order Details API
      if (currentOrderId) {
        try {
          const detailsRes = await axiosInstance.get(`/accounts/orderdetails/?order=${currentOrderId}`);
          setOrderDetails(detailsRes?.data?.results || []);
        } catch (err) {
          console.error("Failed to fetch order details", err);
        }
      }

      // 3. Call Delivery Charges API
      try {
        const deliveryPayload = { items: deliveryItemsPayload };
        const res = await axiosInstance.post(`/accounts/deliverycharges/`, deliveryPayload);
        const details = res.data?.details || [];
        const totalDelivery = details.reduce((sum, item) => sum + (item.delivery_charges || 0), 0);
        const finalDeliveryCharge = mode === "self-transport" ? 0 : totalDelivery;
        setDeliveryCharge(finalDeliveryCharge);
        setDisplayedDeliveryCharge(finalDeliveryCharge);
        localStorage.setItem("deliveryCharge", finalDeliveryCharge.toString());
      } catch (err) {
        console.error("Failed to fetch delivery charges", err);
        setDeliveryCharge(0);
        setDisplayedDeliveryCharge(0);
        localStorage.setItem("deliveryCharge", "0");
      }
    } catch (err) {
      console.error("Failed to sync order and delivery charges", err);
    } finally {
      setLoadingOrder(false);
      setUpdatingDeliveryMode(false);
    }
  };

  useEffect(() => {
    if (!orderInfo || !orderInfo.coupon_code) return;
    const list = Array.isArray(orderInfo.coupon_code) ? orderInfo.coupon_code : [];
    const saleC = list.find(c => c.coupon_type === "sale" && !c.is_deleted && c.status_flag !== false);
    if (saleC && (saleC.coupon_code || saleC.code) && !appliedSaleCoupon) {
      const cCode = saleC.coupon_code || saleC.code;
      setAppliedSaleCoupon({ code: cCode, id: saleC.coupon_applied ?? saleC.id });
      setCouponInputSale(cCode);
    }
    const rentC = list.find(c => (c.coupon_type === "rental" || c.coupon_type === "rent") && !c.is_deleted && c.status_flag !== false);
    if (rentC && (rentC.coupon_code || rentC.code) && !appliedRentalCoupon) {
      const cCode = rentC.coupon_code || rentC.code;
      setAppliedRentalCoupon({ code: cCode, id: rentC.coupon_applied ?? rentC.id });
      setCouponInputRental(cCode);
    }
  }, [orderInfo]);

  useEffect(() => {
    if (allItems.length > 0) {
      syncOrderAndCharges(deliveryMode);
    }
  }, []);

  const handleDeliveryModeChange = (mode) => {
    setDeliveryMode(mode);
    localStorage.setItem("deliveryMode", mode);
    setUpdatingDeliveryMode(true);
    syncOrderAndCharges(mode);
  };

  // Simply navigate to payment — order is created on the Payment page when user places order
  const handleProceedToPayment = () => {
    navigate("/payment");
  };


  if (allItems.length === 0) {
    return (
      <main className="checkout-page">
        <section className="checkout-empty">
          <h1>Your cart is empty</h1>
          <p>Add products to your cart before checkout.</p>
          <button type="button" onClick={() => navigate("/buy")}>Browse products</button>
        </section>
      </main>
    );
  }

  const orderData = Array.isArray(orderInfo) ? orderInfo[0] : (orderInfo?.order || orderInfo);
  const displaySubtotal = orderData?.total_amount != null ? Number(orderData.total_amount) : subtotal;
  const displayDelivery = displayedDeliveryCharge;

  // Guarantee the math matches exactly
  const totalDiscount = saleDiscount + rentalDiscount;
  const grandTotal = Math.max(0, (displaySubtotal + (displayDelivery || 0)) - totalDiscount);

  const hasSaleCoupons = buyCart.length > 0 && saleCoupons.length > 0;
  const hasRentalCoupons = rentCart.length > 0 && rentalCoupons.length > 0;



  const groupedItems = [];
  allItems.forEach(item => {
    let group = groupedItems.find(g => g.id === item.id);
    if (!group) {
      group = { id: item.id, name: item.name, image: item.image, items: [] };
      groupedItems.push(group);
    }
    group.items.push(item);
  });

  return (
    <main className="checkout-page template-construction">
      <div className="checkout-wrap">
        <Breadcrumbs items={[
          { label: "Home", path: "/" },
          { label: "Cart", path: "/cart" },
          { label: "Checkout", path: "" }
        ]} />

        {/* <button className="checkout-back" type="button" onClick={() => navigate("/cart")} style={{ background: "none", border: "none", cursor: "pointer", color: "#6B7280", fontWeight: 600, display: "flex", alignItems: "center", gap: "8px", marginBottom: "20px" }}>
          <FiArrowLeft /> Back to cart
        </button> */}

        <div className="checkout-layout" style={{ display: "grid", gridTemplateColumns: "1fr 390px", gap: "28px", alignItems: "flex-start" }}>
          <section className="checkout-panel">
            <div className="checkout-heading" style={{ marginBottom: "24px" }}>
              <span style={{ color: "#ff8c00", fontWeight: 800, textTransform: "uppercase", fontSize: "12px", letterSpacing: "1px" }}>Secure checkout</span>
              <h1 style={{ fontSize: "28px", fontWeight: 700, margin: "4px 0 8px", color: "#111" }}>Review your order</h1>
              <p style={{ color: "#6b7280", fontSize: "14px", margin: 0 }}>Confirm your cart before placing the order. Delivery and payment details can be finalized next.</p>
            </div>

            <div className="checkout-items">
              {groupedItems.map(group => (
                <div className="cart-product-group" key={group.id} style={{ marginBottom: "20px" }}>
                  <div className="cart-product-header">
                    <div className="d-flex align-items-center">
                      <div className="group-img-btn">
                        <img src={group.image} alt={group.name} className="group-image" />
                      </div>
                      <div className="group-info ms-3">
                        <h3 className="group-title">{group.name}</h3>
                        <span className="group-config-count">Configurations ({group.items.length})</span>
                      </div>
                    </div>
                  </div>
                  <div className="cart-product-items">
                    {group.items.map(item => {
                      const isRent = item.cartType?.toLowerCase() !== "sale";
                      const tagText = isRent ? (item.selectedPackage?.package_name?.toUpperCase() || "RENTAL") : "BUY";
                      const tagDesc = isRent ? `${item.selectedPackage?.package_name || "Rental"} Package` : "Outright Purchase";
                      let duration = "-";
                      if (isRent) {
                        const count = Number(item.duration ?? item.duration_value ?? item.selectedPackage?.duration_value ?? 1) || 1;
                        const planKey = (item.rentalPlan || item.selectedPackage?.package_name || item.package_name || "Daily").toLowerCase();
                        let unit = "day";
                        let plural = "days";
                        if (planKey.includes("week")) { unit = "week"; plural = "weeks"; }
                        else if (planKey.includes("month")) { unit = "month"; plural = "months"; }
                        else if (planKey.includes("annu") || planKey.includes("year")) { unit = "year"; plural = "years"; }
                        duration = `${count} ${count === 1 ? unit : plural}`;
                      }

                      return (
                        <div className="cart-row-item" key={`${item.cartType}-${item.cartItemId || item.id}`}>
                          <div className="row-color-bar" style={{ backgroundColor: isRent ? "#5A67D8" : "#48BB78" }}></div>
                          <div className="row-content">
                            <div className="row-col row-type" style={{ width: !isRent ? "25%" : undefined }}>
                              <span className={`tag ${isRent ? "rent" : "buy"}`}>{tagText}</span>
                              <p className="type-desc">{tagDesc}</p>
                            </div>
                            {isRent && (
                              <>
                                <div className="row-col row-date">
                                  <span className="col-label">DATE</span>
                                  <p className="col-value">
                                    {(() => {
                                      const rawDate = item.fromDate || item.startDate || item.rental_start_date;
                                      if (!rawDate || rawDate === "-") return "-";
                                      const parts = String(rawDate).split("-");
                                      return parts.length === 3 ? `${parts[1]}/${parts[2]}/${parts[0]}` : rawDate;
                                    })()}
                                  </p>
                                </div>
                                <div className="row-col row-duration">
                                  <span className="col-label">DURATION</span>
                                  <p className="col-value">{duration}</p>
                                </div>
                              </>
                            )}
                            <div className="row-col row-qty">
                              <span className="col-label">QTY</span>
                              <span className="qty-value" style={{ fontWeight: 600, fontSize: "14px", color: "#111" }}>{item.qty || 1} </span>
                            </div>
                            <div className="row-col row-price">
                              <span className="col-label" style={{ textAlign: "right", width: "100%" }}>TOTAL</span>
                              <p className="price-val" style={{ textAlign: "right", width: "100%" }}>{formatMoney(getLinePrice(item) * (item.qty || 1))}</p>
                            </div>
                            <div className="row-col row-actions" style={{ visibility: "hidden", width: "10%" }}></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <aside className="checkout-summary">
            <div className="order-summary-box" style={{ borderLeft: "none" }}>

              {/* ── Coupon section at top of sidebar ── */}
              {(hasSaleCoupons || hasRentalCoupons) && (
                <div className="co-coupons-wrapper" style={{ marginTop: 0, paddingTop: 0, borderTop: "none", marginBottom: "20px" }}>
                  <h3 className="co-coupons-header"> Coupons & Offers</h3>
                  {hasSaleCoupons && (
                    <CouponSection
                      type="sale"
                      coupons={saleCoupons}
                      applied={appliedSaleCoupon}
                      onApply={applyCoupon}
                      onRemove={removeCoupon}
                      expanded={couponExpandSale}
                      setExpanded={setCouponExpandSale}
                      inputVal={couponInputSale}
                      setInputVal={setCouponInputSale}
                      applyingCouponId={applyingCouponId}
                      orderInfo={orderInfo}
                      couponError={couponError}
                    />
                  )}
                  {hasRentalCoupons && (
                    <CouponSection
                      type="rental"
                      coupons={rentalCoupons}
                      applied={appliedRentalCoupon}
                      onApply={applyCoupon}
                      onRemove={removeCoupon}
                      expanded={couponExpandRental}
                      setExpanded={setCouponExpandRental}
                      inputVal={couponInputRental}
                      setInputVal={setCouponInputRental}
                      applyingCouponId={applyingCouponId}
                      orderInfo={orderInfo}
                      couponError={couponError}
                    />
                  )}
                  <div className="summary-divider" style={{ marginBottom: "20px" }}></div>
                </div>
              )}

              <div className="checkout-summary-head" style={{ display: "flex", alignItems: "center", marginBottom: "24px" }}>
                <FiShield size={28} color="#3B82F6" />
                <div style={{ marginLeft: "12px" }}>
                  <h2 className="order-summary-title" style={{ margin: 0, letterSpacing: 0, textTransform: "none", fontSize: "18px", color: "#111" }}>Order summary</h2>
                  <p style={{ margin: 0, color: "#6B7280", fontSize: "13px", fontWeight: 600 }}>{totalQuantity} unit{totalQuantity === 1 ? "" : "s"} in this order</p>
                </div>
              </div>
              <div className="summary-line">
                <span>Subtotal</span>
                <span className="summary-line-val bold">{formatMoney(displaySubtotal)}</span>
              </div>
              <div className="summary-line">
                <span>
                  {deliveryMode === "self-transport" ? "Transport" : "Delivery"}
                </span>

                <span className="summary-line-val bold">
                  {updatingDeliveryMode
                    ? "Updating..."
                    : displayDelivery === null
                      ? "Calculating..."
                      : deliveryMode === "self-transport"
                        ? "Self Pickup"
                        : displayDelivery === 0
                          ? "FREE"
                          : formatMoney(displayDelivery)}
                </span>
              </div>
              {totalDiscount > 0 && (
                <div className="summary-line co-discount-line">
                  <span>Coupon Discount</span>
                  <span className="summary-line-val bold co-discount-val">-{formatMoney(totalDiscount)}</span>
                </div>
              )}

              <div className="summary-divider"></div>

              <div className="summary-total">
                <span className="summary-total-label">Total</span>
                <span className="summary-total-val">{formatMoney(grandTotal)}</span>
              </div>

              <div className="choose-transport" style={{ display: "flex", gap: "10px", margin: "16px 0" }}>
                <button
                  type="button"
                  className={`transpport-btn ${deliveryMode === "company-transport" ? "active" : ""}`}
                  onClick={() => handleDeliveryModeChange("company-transport")}
                  disabled={loadingOrder}
                  style={{
                    flex: 1,
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: "1px solid",
                    borderColor: deliveryMode === "company-transport" ? "#fe7900" : "#d1d5db",
                    backgroundColor: deliveryMode === "company-transport" ? "#fe7900" : "#fff",
                    color: deliveryMode === "company-transport" ? "#fff" : "#374151",
                    fontWeight: 600,
                    fontSize: "13px",
                    cursor: loadingOrder ? "not-allowed" : "pointer"
                  }}
                >
                  Company Transport
                </button>
                <button
                  type="button"
                  className={`transpport-btn ${deliveryMode === "self-transport" ? "active" : ""}`}
                  onClick={() => handleDeliveryModeChange("self-transport")}
                  disabled={loadingOrder}
                  style={{
                    flex: 1,
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: "1px solid",
                    borderColor: deliveryMode === "self-transport" ? "#fe7900" : "#d1d5db",
                    backgroundColor: deliveryMode === "self-transport" ? "#fe7900" : "#fff",
                    color: deliveryMode === "self-transport" ? "#fff" : "#374151",
                    fontWeight: 600,
                    fontSize: "13px",
                    cursor: loadingOrder ? "not-allowed" : "pointer"
                  }}
                >
                  Self Transport
                </button>
              </div>

              <button
                className="checkout-btn"
                type="button"
                onClick={handleProceedToPayment}
                disabled={displayDelivery === null || loadingOrder}
              >
                <FiCheckCircle style={{ marginRight: "8px" }} /> {loadingOrder ? "Proceed to Payment" : "Proceed to Payment"}
              </button>

              <div className="secure-checkout">
                <FiLock /> Secure checkout
              </div>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}

export default Checkout;
